import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  BarChart,
  Bar,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  LabelList
} from 'recharts';
import { Headphones } from 'lucide-react';

// ============== TV SPEEDOMETER (large) ==============
const KPTGauge = ({ value, label }) => {
  // Farvegrænser:
  //   0,00 – 0,39  → rød
  //   0,40 – 0,42  → gul
  //   0,43 og over → grøn
  const getColor = (val) => {
    if (val >= 0.43) return '#22c55e';
    if (val >= 0.40) return '#eab308';
    return '#ef4444';
  };

  const color = getColor(value);
  const percentage = Math.min((value / 0.7) * 100, 100);
  const angle = (percentage / 100) * 180;
  
  const radius = 44;
  const strokeWidth = 8;
  const center = 50;
  
  const polarToCartesian = (cx, cy, r, a) => {
    const rad = (a - 180) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
  };

  // Vinkler beregnet fra max 0,7 → 180°:
  //   0,40 → 102,86°  (slutning af rød / start af gul)
  //   0,43 → 110,57°  (slutning af gul / start af grøn)
  return (
    <div className="flex flex-col items-center">
      <svg width={300} height={160} viewBox="0 0 100 55">
        <path d={describeArc(center, 50, radius, 0, 180)} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={strokeWidth} strokeLinecap="round" />
        <path d={describeArc(center, 50, radius, 110.57, 180)} fill="none" stroke="#22c55e" strokeWidth={strokeWidth} strokeLinecap="round" opacity="0.3" />
        <path d={describeArc(center, 50, radius, 102.86, 110.57)} fill="none" stroke="#eab308" strokeWidth={strokeWidth} strokeLinecap="round" opacity="0.3" />
        <path d={describeArc(center, 50, radius, 0, 102.86)} fill="none" stroke="#ef4444" strokeWidth={strokeWidth} strokeLinecap="round" opacity="0.3" />
        {angle > 0 && (
          <path d={describeArc(center, 50, radius, 0, Math.min(angle, 180))} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
        )}
        <line 
          x1={center} y1="50" 
          x2={center + (radius - 8) * Math.cos((angle - 180) * Math.PI / 180)} 
          y2={50 + (radius - 8) * Math.sin((angle - 180) * Math.PI / 180)} 
          stroke={color} strokeWidth="2.5" strokeLinecap="round" 
        />
        <circle cx={center} cy="50" r="4" fill={color} />
      </svg>
      <span className="text-5xl font-black" style={{ color }}>{value.toFixed(2)}</span>
      <span className="text-lg text-white/50 mt-2 font-medium">{label}</span>
    </div>
  );
};

// ============== TV CAPACITY BAR (large) ==============
const CapacityBar = ({ label, booked, available, percentage }) => {
  const color = percentage >= 85 ? '#22c55e' : percentage >= 70 ? '#eab308' : '#ef4444';
  
  return (
    <div className="bg-[#1e1e1e] rounded-xl p-4 border border-white/5 flex flex-col justify-center">
      <div className="flex justify-between items-center mb-2">
        <span className="text-base text-white/70 font-medium">{label}</span>
        <span className="text-lg font-bold" style={{ color }}>{percentage}%</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-2xl font-bold text-white w-12">{booked}</span>
        <div className="flex-1 h-4 bg-white/10 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          />
        </div>
        <span className="text-2xl font-bold text-white/30 w-12 text-right">{available}</span>
      </div>
    </div>
  );
};

export const Kundeservice = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [newBookingsData, setNewBookingsData] = useState(null);
  const [calendarCapacityData, setCalendarCapacityData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  const AUTO_REFRESH_INTERVAL = 60000;

  const fetchNewBookingsData = useCallback(async () => {
    try {
      const response = await api.get('/bookings/new-this-week');
      setNewBookingsData(response.data);
    } catch (err) {
      console.error('Error fetching new bookings data:', err);
      if (err.response?.status === 401) navigate('/login');
    }
  }, [navigate]);

  const fetchCalendarCapacityData = useCallback(async () => {
    try {
      const response = await api.get('/bookings/calendar-capacity');
      setCalendarCapacityData(response.data);
    } catch (err) {
      console.error('Error fetching calendar capacity data:', err);
      if (err.response?.status === 401) navigate('/login');
    }
  }, [navigate]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchNewBookingsData(), fetchCalendarCapacityData()]);
    setLastRefresh(new Date());
    setLoading(false);
  }, [fetchNewBookingsData, fetchCalendarCapacityData]);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  useEffect(() => {
    const interval = setInterval(() => fetchAllData(), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  // Chart data - Bookinger pr. dag
  const chartData = useMemo(() => {
    if (!newBookingsData?.daily_data) return [];
    const weeklyTarget = Number(newBookingsData?.weekly_target) || 252;
    const dailyTarget = Math.round(weeklyTarget / 7);
    
    return newBookingsData.daily_data.map((d) => {
      const dateObj = new Date(d.dato + 'T00:00:00');
      const dayNum = dateObj.getDate();
      const monthNum = dateObj.getMonth() + 1;
      return {
        dag: `${d.dag} ${dayNum}/${monthNum}`,
        bookinger: d.nye_bookinger ?? 0,
        maal: dailyTarget
      };
    });
  }, [newBookingsData]);

  // Chart data - Akkumuleret
  const akkumuleretData = useMemo(() => {
    if (!newBookingsData?.daily_data) return [];
    return newBookingsData.daily_data.map(day => ({
      dag: day.dag,
      akkumuleret: day.akkumuleret,
      maal: day.maal
    }));
  }, [newBookingsData]);

  // Capacity data
  const thisWeek = useMemo(() => ({
    booked: Number(calendarCapacityData?.this_week?.bookinger) || 0,
    available: Number(calendarCapacityData?.this_week?.ledige_tider) || 0,
    percentage: Number(calendarCapacityData?.this_week?.belaegning) || 0
  }), [calendarCapacityData]);

  const nextWeek = useMemo(() => ({
    booked: Number(calendarCapacityData?.next_week?.bookinger) || 0,
    available: Number(calendarCapacityData?.next_week?.ledige_tider) || 0,
    percentage: Number(calendarCapacityData?.next_week?.belaegning) || 0
  }), [calendarCapacityData]);

  const week3 = useMemo(() => ({
    booked: Number(calendarCapacityData?.week_3?.bookinger) || 0,
    available: Number(calendarCapacityData?.week_3?.ledige_tider) || 0,
    percentage: Number(calendarCapacityData?.week_3?.belaegning) || 0
  }), [calendarCapacityData]);

  const todaysBookings = newBookingsData?.today_bookings ?? 0;
  const dailyTarget = Math.round((Number(newBookingsData?.weekly_target) || 252) / 7);
  const todayColor = todaysBookings >= dailyTarget ? '#22c55e' : todaysBookings >= dailyTarget * 0.7 ? '#eab308' : '#ef4444';

  if (loading && !newBookingsData) {
    return (
      <div className="h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white/40 text-2xl">Indlaeser dashboard...</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#0a0a0a] flex flex-col overflow-hidden" data-testid="kundeservice">
      {/* Minimal TV header */}
      <header className="px-8 py-3 flex items-center justify-between flex-shrink-0 border-b border-white/5">
        <div className="flex items-center gap-3">
          <Headphones className="w-7 h-7 text-[#1E6F7A]" />
          <h1 className="text-2xl font-bold text-white tracking-tight">Kundeservice</h1>
        </div>
        <div className="flex items-center gap-6">
          <span className="text-base text-white/30">
            Opdateret {lastRefresh.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" title="Live" />
        </div>
      </header>

      {/* Full-screen dashboard grid */}
      <main className="flex-1 p-6 grid grid-cols-12 gap-5 min-h-0">
        
        {/* LEFT COLUMN: Bookinger i dag (hero metric) + Kapacitet */}
        <div className="col-span-3 flex flex-col gap-5">
          
          {/* Hero: Bookinger i dag */}
          <div className="bg-[#141414] rounded-2xl border border-white/5 flex-1 flex flex-col items-center justify-center p-6" data-testid="bookinger-idag-hero">
            <span className="text-lg text-white/40 uppercase tracking-widest mb-4">Bookinger i dag</span>
            <span className="font-black leading-none" style={{ color: todayColor, fontSize: 'clamp(5rem, 12vw, 10rem)' }}>
              {todaysBookings}
            </span>
            <div className="mt-4 flex items-center gap-2">
              <span className="text-xl text-white/30">Maal:</span>
              <span className="text-xl text-white/50 font-bold">{dailyTarget}</span>
            </div>
            <div className="mt-3 w-3/4 h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-700"
                style={{ 
                  width: `${Math.min((todaysBookings / dailyTarget) * 100, 100)}%`, 
                  backgroundColor: todayColor 
                }}
              />
            </div>
          </div>

          {/* Kapacitet bars */}
          <div className="flex flex-col gap-3">
            <CapacityBar label="Denne uge" booked={thisWeek.booked} available={thisWeek.available} percentage={thisWeek.percentage} />
            <CapacityBar label="Naeste uge" booked={nextWeek.booked} available={nextWeek.available} percentage={nextWeek.percentage} />
            <CapacityBar label="Uge 3" booked={week3.booked} available={week3.available} percentage={week3.percentage} />
          </div>
        </div>

        {/* CENTER: Two charts stacked */}
        <div className="col-span-6 flex flex-col gap-5">
          
          {/* Chart 1: Bookinger pr. dag */}
          <div className="flex-1 bg-[#141414] rounded-2xl p-6 border border-white/5 flex flex-col min-h-0">
            <h3 className="text-base font-semibold text-white/60 mb-3 uppercase tracking-wide">Bookinger pr. dag (sidste 7 dage)</h3>
            <div className="flex-1 min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 25, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis 
                    dataKey="dag" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 14, fontWeight: 500 }} 
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 14 }} 
                    width={45}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#252525', 
                      border: '1px solid rgba(255,255,255,0.1)', 
                      borderRadius: '8px',
                      fontSize: 14
                    }}
                    cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                    formatter={(value) => [value, 'Bookinger']}
                  />
                  <ReferenceLine y={dailyTarget} stroke="#ef4444" strokeDasharray="6 4" strokeWidth={2} label={{ value: `Maal: ${dailyTarget}`, fill: '#ef4444', fontSize: 13, position: 'right' }} />
                  <Bar 
                    dataKey="bookinger" 
                    fill="#22c55e" 
                    radius={[6, 6, 0, 0]}
                    maxBarSize={60}
                  >
                    <LabelList dataKey="bookinger" position="top" fill="rgba(255,255,255,0.8)" fontSize={16} fontWeight={600} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart 2: Akkumuleret */}
          <div className="flex-1 bg-[#141414] rounded-2xl p-6 border border-white/5 flex flex-col min-h-0">
            <h3 className="text-base font-semibold text-white/60 mb-3 uppercase tracking-wide">Nye bookinger akkumuleret (sidste 7 dage)</h3>
            <div className="flex-1 min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={akkumuleretData} margin={{ top: 25, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis 
                    dataKey="dag" 
                    stroke="rgba(255,255,255,0.3)"
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 14, fontWeight: 500 }} 
                  />
                  <YAxis 
                    stroke="rgba(255,255,255,0.3)"
                    tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 14 }} 
                    width={45}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1A1A1A', 
                      border: '1px solid rgba(255,255,255,0.1)', 
                      borderRadius: '8px',
                      fontSize: 14
                    }}
                    formatter={(value, name) => [value ?? 'N/A', name === 'akkumuleret' ? 'Resultat' : 'Maal']}
                  />
                  <Bar 
                    dataKey="akkumuleret" 
                    fill="#1E6F7A" 
                    name="akkumuleret"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={60}
                  >
                    <LabelList dataKey="akkumuleret" position="top" fill="rgba(255,255,255,0.7)" fontSize={15} fontWeight={600} />
                  </Bar>
                  <Line 
                    type="monotone"
                    dataKey="maal" 
                    stroke="#B3261E" 
                    strokeWidth={3}
                    dot={{ fill: '#B3261E', strokeWidth: 2, r: 5 }}
                    name="maal"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: KPT Gauges + Ugestatus */}
        <div className="col-span-3 flex flex-col gap-5">
          
          {/* KPT Gauges */}
          <div className="bg-[#141414] rounded-2xl p-6 border border-white/5 flex flex-col items-center justify-evenly flex-1">
            <span className="text-lg text-white/40 uppercase tracking-widest font-semibold">Kunde pr. time</span>
            <KPTGauge 
              value={calendarCapacityData?.this_week?.kpt || 0} 
              label="Denne uge" 
            />
            <div className="w-2/3 h-px bg-white/10" />
            <KPTGauge 
              value={calendarCapacityData?.next_week?.kpt || 0} 
              label="Naeste uge" 
            />
            <div className="w-2/3 h-px bg-white/10" />
            <KPTGauge
              value={calendarCapacityData?.month_to_date?.kpt || 0}
              label={(() => {
                const MONTHS_DA = ['Januar','Februar','Marts','April','Maj','Juni','Juli','August','September','Oktober','November','December'];
                const mNum = calendarCapacityData?.month_to_date?.month_number;
                const mName = (mNum && mNum >= 1 && mNum <= 12) ? MONTHS_DA[mNum - 1] : 'Måneden';
                return `${mName} til dato`;
              })()}
            />
          </div>
        </div>

      </main>
    </div>
  );
};
